import {
	createContext,
	type ReactNode,
	useContext,
	useEffect,
	useState,
} from "react";
import { usuarioLogout } from "@/services/analytics/events";
import { deleteCookie, getCookie, setCookie } from "@/utils/cookieUtils";

export type UserRole =
	| "admin"
	| "manager"
	| "driver"
	| "telemetryadmin"
	| "telemetrymember";

export type User = {
	id: string;
	role: UserRole[];
	fullName?: string;
	email?: string;
};

type AuthContextProps = {
	user: User | null;
	loading: boolean;
	login: (
		token: string,
		user?: User | null,
		expiresAt?: string | number
	) => void;
	logout: () => void;
};

const AuthContext = createContext<AuthContextProps | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
	const [user, setUser] = useState<User | null>(null);
	const [loading, setLoading] = useState(true);

	const normalizeRoles = (roles: unknown): UserRole[] => {
		if (!roles) return [];
		const arr = Array.isArray(roles)
			? roles
			: typeof roles === "string"
				? [roles]
				: [];
		return arr
			.filter((r): r is string => r != null && typeof r === "string")
			.map((r) => r.toLowerCase() as UserRole)
			.filter((r) =>
				[
					"admin",
					"manager",
					"driver",
					"telemetryadmin",
					"telemetrymember",
				].includes(r)
			);
	};

	useEffect(() => {
		const token = getCookie("token");

		if (!token) {
			localStorage.removeItem("user");
			setUser(null);
			setLoading(false);
			return;
		}

		try {
			const storedUser = localStorage.getItem("user");
			if (storedUser) {
				const parsedUser = JSON.parse(storedUser);

				if (parsedUser && typeof parsedUser === "object") {
					parsedUser.role = normalizeRoles(parsedUser.role);
					setUser(parsedUser);
				} else {
					localStorage.removeItem("user");
					setUser(null);
				}
			}
		} catch (err) {
			console.error("Erro ao parsear usuário do localStorage", err);
			localStorage.removeItem("user");
			setUser(null);
		}

		setLoading(false);
	}, []);

	const login = (
		token: string,
		user?: User | null,
		expiresAt?: string | number
	) => {
		try {
			setCookie("token", token, expiresAt);

			if (user) {
				const normalizedUser: User = {
					...user,
					role: normalizeRoles(user.role),
				};

				localStorage.setItem("user", JSON.stringify(normalizedUser));
				setUser(normalizedUser);
			}
		} catch (err) {
			console.error("Erro ao salvar dados de login", err);
		}
	};

	const logout = () => {
		usuarioLogout();
		deleteCookie("token");
		localStorage.removeItem("user");
		setUser(null);
	};

	return (
		<AuthContext.Provider value={{ user, loading, login, logout }}>
			{children}
		</AuthContext.Provider>
	);
}

export function useAuth() {
	const ctx = useContext(AuthContext);
	if (!ctx) throw new Error("useAuth deve ser usado dentro de AuthProvider");
	return ctx;
}
